import { DocumentNode, gql } from '@apollo/client';
import { Badge, Stack, StackProps, Text, Tooltip } from '@chakra-ui/react';
import * as React from 'react';

import { AlertCircleIcon } from '@/imports/ui/chakra/feather';

import { PaymentMethod_PaymentMethodFragment } from './graphql-types/PaymentMethod_PaymentMethodFragment';

export const PaymentMethod: React.FC<
  {
    paymentMethod: PaymentMethod_PaymentMethodFragment;
  } & StackProps
> & { paymentMethodFragment: DocumentNode } = ({ paymentMethod, ...props }) => {
  if (paymentMethod.__typename === 'PaymentMethod_UsBankAccount') {
    return (
      <Stack spacing="0" {...props}>
        <Stack direction="row" alignItems="center">
          <Text
            textStyle="detailStrong"
            color="gray.500"
            textTransform="uppercase"
          >
            {paymentMethod.bankName}
          </Text>
          {paymentMethod.isDefault && <Badge colorScheme="gray">Default</Badge>}
          {!paymentMethod.verified && (
            <Badge colorScheme="red">Unverified</Badge>
          )}
          {paymentMethod.hasFailed && (
            <Tooltip label="We were unable to charge this payment method.">
              <AlertCircleIcon color="red.500" />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center">
          <Text textStyle="bodyStrong">
            **** {paymentMethod.bankAccountLastFourDigits}
          </Text>
        </Stack>
      </Stack>
    );
  }

  if (paymentMethod.__typename === 'PaymentMethod_Card') {
    return (
      <Stack spacing="0" {...props}>
        <Stack direction="row" alignItems="center">
          <Text
            textStyle="detailStrong"
            color="gray.500"
            textTransform="uppercase"
          >
            {paymentMethod.brand || 'CREDIT CARD'}
          </Text>
          {paymentMethod.isDefault && <Badge colorScheme="gray">Default</Badge>}
          {!paymentMethod.verified && (
            <Badge colorScheme="red">Unverified</Badge>
          )}
          {paymentMethod.hasFailed && (
            <Tooltip label="We were unable to charge this payment method.">
              <AlertCircleIcon color="red.500" />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center">
          <Text textStyle="bodyStrong">
            **** **** **** {paymentMethod.creditCardLastFourDigits}
          </Text>

          <Text textStyle="detailStrong" color="gray.500">
            EXP {paymentMethod.expirationMonth}/{paymentMethod.expirationYear}
          </Text>
        </Stack>
      </Stack>
    );
  }

  return null;
};

PaymentMethod.paymentMethodFragment = gql`
  fragment PaymentMethod_PaymentMethodFragment on PaymentMethod {
    _id
    canCurrentUserRemoveThisPaymentMethod
    hasFailed
    isDefault
    verified
    ... on PaymentMethod_Card {
      creditCardLastFourDigits: lastFourDigits
      expirationMonth
      expirationYear
      brand
    }
    ... on PaymentMethod_UsBankAccount {
      bankAccountLastFourDigits: lastFourDigits
      bankName
    }
  }
`;
